import React, { Component } from 'react';
import styles from './TasksProjectIssuesPage.module.scss';
import Icon from '@ant-design/icons';
import { ReactComponent as BackSvg } from '../../../../resources/images/back.svg';
import { ReactComponent as RefreshSvg } from '../../../../resources/images/refresh.svg';
import LayoutComponent from '../../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../../context/CustomContext';
import { FormattedDate, FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Button, Descriptions, Space, Table, Tooltip } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { TaskProjectIssue, TaskProjectIssueAction } from '../../../../model/entities';
import { Page } from '../../../../model/elements';
import tableService from '../../../../services/TableService';
import { Link, RouteComponentProps } from 'react-router-dom';
import Search from 'antd/lib/input/Search';
import taskProjectIssueActionApi from '../../../../api/TaskProjectIssueActionApi';
import notificationService from '../../../../services/NotificationService';
import { TaskStatusType } from '../../../../model/types';
import TaskStatusComponent from '../../../../components/TaskStatusComponent/TaskStatusComponent';
import taskProjectIssuesApi from '../../../../api/TaskProjectIssueApi';
import ProjectIssueStatusComponent from '../../../Setup/ProjectIssuesPage/ProjectIssueStatusComponent/ProjectIssueStatusComponent';

class TasksProjectIssuesPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;

    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        this.list();
    }

    /** METHODS **/

    list = async () => {
        const { taskProjectIssueActionPage, searchText } = this.state;

        try {
            this.setState({ loading: true });
            const task = await taskProjectIssuesApi.get(+this.props.match.params.id);
            this.setState({ task });

            const page = 0;
            const size = taskProjectIssueActionPage?.size || tableService.pageSize;
            const sortField = taskProjectIssueActionPage?.sort.field || 'title';
            const sortOrder = taskProjectIssueActionPage?.sort.order || true;
            await this.listTaskProjectIssueActions(page, size, sortField, sortOrder, task.id!, searchText);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    paginate = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        const { task, searchText } = this.state;
        try {
            this.setState({ loading: true });
            const page = pagination.current! - 1;
            const pageSize = pagination.pageSize!;
            const sortField = sorter.field;
            const sortOrder = sorter.order === 'ascend';
            this.listTaskProjectIssueActions(page, pageSize, sortField, sortOrder, task!.id!, searchText);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    search = async (searchText: string) => {
        const { task, taskProjectIssueActionPage } = this.state;
        try {
            this.setState({ loading: true, searchText });
            const page = 0;
            const pageSize = taskProjectIssueActionPage!.size;
            const sortField = taskProjectIssueActionPage!.sort.field!;
            const sortOrder = taskProjectIssueActionPage!.sort.order!;
            this.listTaskProjectIssueActions(page, pageSize, sortField, sortOrder, task!.id!, searchText);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    listTaskProjectIssueActions = async (
        page: number,
        size: number,
        sortField: string,
        sortOrder: boolean,
        taskId: number,
        searchText?: string,
    ) => {
        const taskProjectIssueActionPage = await taskProjectIssueActionApi.list(
            page,
            size,
            sortField,
            sortOrder,
            taskId,
            searchText,
        );
        this.setState({ taskProjectIssueActionPage });
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        return (
            <>
                {this.renderSummary()}
                {this.renderToolbar()}
                {this.renderTable()}
            </>
        );
    };

    renderSummary = (): React.ReactElement | undefined => {
        const { task } = this.state;

        return (
            <Descriptions
                title={
                    <>
                        <span className={styles.title}>
                            #{task?.id} - {task?.name}
                        </span>
                        <TaskStatusComponent status={task?.status} />
                        <Space>
                            <Tooltip title={<FormattedMessage id="button.back" />}>
                                <Link to="/tasks">
                                    <Button type="ghost" size="small" icon={<Icon component={BackSvg} />} />
                                </Link>
                            </Tooltip>
                            <Tooltip title={<FormattedMessage id="button.refresh" />}>
                                <Button
                                    type="ghost"
                                    size="small"
                                    icon={<Icon component={RefreshSvg} />}
                                    onClick={this.list}
                                />
                            </Tooltip>
                        </Space>
                    </>
                }
                column={2}
                size="small"
                className={styles.summary}
            >
                <Descriptions.Item label={<FormattedMessage id="task.source" />}>
                    {task?.sourceHub?.name} &gt; {task?.sourceProject?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.target" />}>
                    {task?.targetHub?.name} &gt; {task?.targetProject?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.audit.created" />}>
                    <FormattedDate
                        value={task?.audit?.created as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.audit.updated" />}>
                    <FormattedDate
                        value={task?.audit?.updated as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />
                </Descriptions.Item>
                <Descriptions.Item>{task?.description}</Descriptions.Item>
            </Descriptions>
        );
    };

    renderToolbar = (): React.ReactElement | undefined => {
        const { intl } = this.props;
        const placeholder: string = intl.formatMessage({ id: 'taskProjectIssues.search' });

        return (
            <div className="toolbar">
                <Search placeholder={placeholder} onSearch={this.search} size="large" className="search" />
            </div>
        );
    };

    renderAction = (taskProjectIssue: TaskProjectIssueAction): React.ReactElement | string | undefined => {
        return <ProjectIssueStatusComponent status={taskProjectIssue.action.status} />;
    };

    renderTable = (): React.ReactElement | undefined => {
        const { taskProjectIssueActionPage, loading } = this.state;
        const items = taskProjectIssueActionPage ? taskProjectIssueActionPage.content : [];
        const columns: ColumnsType<TaskProjectIssueAction> = [
            {
                title: <FormattedMessage id="taskProjectIssue.title" />,
                dataIndex: 'title',
                key: 'title',
                sorter: true,
            },
            {
                title: <FormattedMessage id="taskProjectIssue.action" />,
                dataIndex: 'action',
                key: 'action',
                width: 200,
                align: 'center',
                render: (action: any, taskProjectIssueAction: TaskProjectIssueAction) =>
                    this.renderAction(taskProjectIssueAction),
            },
            {
                title: <FormattedMessage id="taskProjectIssue.audit.created" />,
                dataIndex: 'auditCreated',
                key: 'created',
                sorter: true,
                width: 240,
                align: 'center',
                render: (value: string, taskProjectIssueAction: TaskProjectIssueAction) => (
                    <FormattedDate
                        value={taskProjectIssueAction.audit!.created as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />
                ),
            },
            {
                title: <FormattedMessage id="taskProjectIssue.status" />,
                dataIndex: 'status',
                key: 'status',
                sorter: true,
                width: 200,
                align: 'center',
                render: (status: TaskStatusType, taskProjectIssueAction: TaskProjectIssueAction) => (
                    <TaskStatusComponent status={status} error={taskProjectIssueAction.error} />
                ),
            },
            {
                title: <FormattedMessage id="taskProjectIssue.commentsStatus" />,
                dataIndex: 'commentsStatus',
                key: 'commentsStatus',
                sorter: true,
                width: 200,
                align: 'center',
                render: (commentsStatus: TaskStatusType) => <TaskStatusComponent status={commentsStatus} />,
            },
            {
                title: <FormattedMessage id="taskProjectIssue.attachmentsStatus" />,
                dataIndex: 'attachmentsStatus',
                key: 'attachmentsStatus',
                sorter: true,
                width: 200,
                align: 'center',
                render: (attachmentsStatus: TaskStatusType, taskProjectIssueAction: TaskProjectIssueAction) => (
                    <Link to={`/task-project-issue-actions/${taskProjectIssueAction.id}`}>
                        <TaskStatusComponent status={attachmentsStatus} />
                    </Link>
                ),
            },
        ];

        return (
            <Table
                dataSource={items}
                columns={columns}
                pagination={tableService.createPagination(taskProjectIssueActionPage, { position: ['bottomRight'] })}
                rowKey="id"
                onChange={this.paginate}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading}
            />
        );
    };

    render() {
        return <LayoutComponent pageId="tasks">{this.renderContent()}</LayoutComponent>;
    }
}
export default injectIntl(TasksProjectIssuesPage);

type ParamsType = { id: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    task?: TaskProjectIssue;
    taskProjectIssueActionPage?: Page<TaskProjectIssueAction>;
    loading?: boolean;
    searchText?: string;
}
